/// <reference types="web-bluetooth" />
import { computed, Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';

/** Characteristic types */
export enum CharType {
  WRITE = 'WRITE',
  NOTIFY = 'NOTIFY',
  READ = 'READ',
}

/** Device characteristic definition */
export interface BleDeviceCharacteristic {
  name: string;
  uuid: string;
  type: CharType;
}

/** Structure for received event data */
export interface EventDataReceived {
  deviceName: string;
  data: DataView | undefined;
  characteristic: string;
}

@Injectable({ providedIn: 'root' })
export class BleService {
  private service_uuid = '';
  private device_characteristics_record: Record<string, BleDeviceCharacteristic> = {};

  // Whether the BLE service is initialized (service UUID + characteristic map)
  private isInitializedSignal = signal(false);
  public serviceIsReady = computed(() => this.isInitializedSignal() && this.isAvailable());

  // Signal indicating Bluetooth availability
  isAvailable = signal(false);

  // Holds connected devices and their characteristics
  connectedDevices = signal<
    Record<string, { device: BluetoothDevice; characteristics: Record<string, BluetoothRemoteGATTCharacteristic> }>
  >({});

  // Computed signal to check if at least one device is connected
  isAtLeastOneBleDeviceConnected = computed(() => Object.keys(this.connectedDevices()).length > 0);

  // Subjects for data (notifications) and disconnection events
  private _onDataReceivedEvent = new Subject<EventDataReceived>();
  public onDataReceivedEvent$ = this._onDataReceivedEvent.asObservable();

  private _onDeviceDisconnected = new Subject<BluetoothDevice>();
  public onDeviceDisconnected$ = this._onDeviceDisconnected.asObservable();

  /**
   * A set of device names that have been manually disconnected (so we know if
   * disconnection was intentional).
   */
  private manualDisconnectedDevices = new Set<string>();

  constructor() {
    if (navigator.bluetooth && typeof navigator.bluetooth.getAvailability === 'function') {
      navigator.bluetooth
        .getAvailability()
        .then(available => this.isAvailable.set(available))
        .catch(() => undefined);
    } else {
      this.isAvailable.set(false);
    }
  }

  /**
   * Initializes the service with a service UUID and characteristic configuration.
   */
  public initService(
    service_uuid: string,
    device_characteristics_record: Record<string, BleDeviceCharacteristic>
  ): void {
    this.service_uuid = service_uuid;
    this.device_characteristics_record = device_characteristics_record;
    this.isInitializedSignal.set(true);
  }

  private isServiceInitialized(): boolean {
    return this.isInitializedSignal();
  }

  //////////////////////////////////////////////////////////////////////////////
  // DEVICE CONNECTION
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Connect to a BLE device using the specified device name prefix.
   * Returns the connected device + its characteristics, or null if connection fails.
   */
  async connectToDevice(
    deviceName: string
  ): Promise<{ device: BluetoothDevice; characteristics: Record<string, BluetoothRemoteGATTCharacteristic> } | null> {
    if (!this.serviceIsReady()) {
      throw new Error('Service must be initialized before calling connectToDevice');
    }
    try {
      const device: BluetoothDevice = await navigator.bluetooth.requestDevice({
        filters: [{ namePrefix: deviceName }],
        optionalServices: [this.service_uuid],
      });
      device.addEventListener('gattserverdisconnected', this.handleDeviceDisconnected.bind(this));

      const server = await this.gattConnectWithRetry(device);
      if (!server) return null;

      const service = await server.getPrimaryService(this.service_uuid);
      await this.setupCharacteristics(device, service);

      return this.connectedDevices()[device.name!];
    } catch (error: any) {
      return null;
    }
  }

  /**
   * (Optional) Reconnect method if you wish to manually call it from outside,
   * but will not be called automatically on unexpected disconnections.
   */
  public async reconnectDevice(
    device: BluetoothDevice
  ): Promise<{ device: BluetoothDevice; characteristics: Record<string, BluetoothRemoteGATTCharacteristic> } | null> {
    if (!this.serviceIsReady()) {
      throw new Error('Service must be initialized before calling reconnectDevice');
    }
    if (!device || !device.name) return null;

    const server = await this.gattConnect(device);
    if (!server) return null;

    const service = await server.getPrimaryService(this.service_uuid);
    await this.setupCharacteristics(device, service);
    return this.connectedDevices()[device.name!];
  }

  /**
   * Attempts to connect to the device's GATT server with up to 5 retries.
   */
  async gattConnectWithRetry(device: BluetoothDevice): Promise<BluetoothRemoteGATTServer | null> {
    if (!this.isServiceInitialized()) {
      throw new Error('Service must be initialized before calling gattConnectWithRetry');
    }
    let attempts = 0;
    while (attempts < 5) {
      try {
        const server = await this.gattConnect(device);
        return server;
      } catch (error) {
        attempts++;
        await this.delay(500);
      }
    }
    throw new Error('gattConnectWithRetry: Maximum attempts reached.');
  }

  /**
   * Connects to the device's GATT server once (no retry).
   */
  public async gattConnect(device: BluetoothDevice): Promise<BluetoothRemoteGATTServer | null> {
    if (!this.isServiceInitialized()) {
      throw new Error('Service must be initialized before calling gattConnect');
    }
    try {
      const server = await device.gatt?.connect();
      if (!server) {
        return null;
      }
      return server;
    } catch (error) {
      return null;
    }
  }

  /**
   * Returns a connected device from the internal state by its name (if it exists).
   */
  public getDeviceByName(name: string) {
    return this.connectedDevices()[name];
  }

  //////////////////////////////////////////////////////////////////////////////
  // CHARACTERISTICS SETUP & DATA HANDLING
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Configures characteristics for the device using the stored characteristic record.
   */
  private async setupCharacteristics(device: BluetoothDevice, service: BluetoothRemoteGATTService): Promise<void> {
    if (!this.isServiceInitialized()) {
      throw new Error('Service must be initialized before calling setupCharacteristics');
    }
    if (!this.device_characteristics_record || typeof this.device_characteristics_record !== 'object') {
      throw new Error('Device characteristic record is undefined or not an object');
    }

    const configs = Object.values(this.device_characteristics_record);
    for (const config of configs) {
      const characteristic = await service.getCharacteristic(config.uuid);

      // Update the device's characteristics map
      const currentDevices = this.connectedDevices();
      const existingDevice = currentDevices[device.name!];
      const updatedCharacteristics = existingDevice
        ? { ...existingDevice.characteristics, [config.uuid]: characteristic }
        : { [config.uuid]: characteristic };

      this.connectedDevices.set({
        ...currentDevices,
        [device.name!]: { device, characteristics: updatedCharacteristics },
      });

      // For NOTIFY characteristics, start notifications and listen for changes
      if (config.type === CharType.NOTIFY) {
        await characteristic.startNotifications();
        characteristic.addEventListener('characteristicvaluechanged', event => {
          const target = event.target as BluetoothRemoteGATTCharacteristic;
          this._onDataReceivedEvent.next({
            deviceName: device.name!,
            data: target.value,
            characteristic: config.name,
          });
        });
      }
    }
  }

  /**
   * Writes data to a specific characteristic of a device.
   */
  async writeToCharacteristic(device: BluetoothDevice, uuid: string, data: BufferSource): Promise<void> {
    if (!this.isServiceInitialized()) {
      throw new Error('Service must be initialized before calling writeToCharacteristic');
    }
    const deviceData = this.connectedDevices()[device.name!];
    if (!deviceData || !deviceData.characteristics[uuid]) {
      return;
    }
    try {
      await deviceData.characteristics[uuid].writeValue(data);
    } catch (error) {
      // Ignoré.
    }
  }

  //////////////////////////////////////////////////////////////////////////////
  // DEVICE DISCONNECTION (NO AUTO-RECONNECT)
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Handler for the 'gattserverdisconnected' event.
   * - Removes the device from state
   * - Emits the disconnection event
   * - Skips any reconnection logic (has been removed)
   */
  private handleDeviceDisconnected(event: Event): void {
    const device = event.target as BluetoothDevice;

    // Remove device from internal state
    this.removeDevice(device);

    // Emit the disconnection event
    this._onDeviceDisconnected.next(device);

    // Check if this device was manually disconnected
    if (this.manualDisconnectedDevices.has(device.name!)) {
      this.manualDisconnectedDevices.delete(device.name!);
      // No further action needed
    }
  }

  /**
   * Removes a device from the internal connected devices state.
   */
  private removeDevice(device: BluetoothDevice): void {
    const currentDevices = this.connectedDevices();
    if (!device.name) return;
    if (device.name in currentDevices) {
      delete currentDevices[device.name];
      this.connectedDevices.set({ ...currentDevices });
    }
  }

  /**
   * Disconnects device(s) by name.
   * -> We add device(s) to manualDisconnectedDevices so we know it was intentional.
   */
  async onDisconnectDevice(deviceName = 'all'): Promise<void> {
    if (!this.isServiceInitialized()) {
      throw new Error('Service must be initialized before calling onDisconnectDevice');
    }

    if (deviceName === 'all') {
      for (const name in this.connectedDevices()) {
        this.manualDisconnectedDevices.add(name);
        this.connectedDevices()[name].device.gatt?.disconnect();
      }
    } else {
      this.manualDisconnectedDevices.add(deviceName);
      const deviceEntry = this.connectedDevices()[deviceName];
      if (deviceEntry) {
        deviceEntry.device.gatt?.disconnect();
      }
    }
  }

  //////////////////////////////////////////////////////////////////////////////
  // HELPER METHODS
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Returns a promise that resolves after a specified delay (in milliseconds).
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
